import React from "react";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  ActionsRow,
  CalendarHeader,
  CalendarPopup,
  DayCell,
  DayHeader,
  DaysGrid,
  MonthYear,
  NavButton,
  RadioControl,
  RadioInput,
  RadioLabel,
  RadioOption,
  RadioRow,
  SectionDivider,
  ToggleGroup,
} from "./datePicker.styles";
import {
  DatePickerMenuPosition,
  DatePickerSize,
  SelectionMode,
} from "./datePicker.types";
import { isSameMonthYear, toDayKey } from "./datePicker.utils";
import { LocalDate } from "@js-joda/core";

interface DatePickerPopupBase {
  position: DatePickerMenuPosition | null;
  zIndex: number;
  size: DatePickerSize;
  mode: SelectionMode;
  currentMonth: LocalDate;
  monthNames: string[];
  dayHeaders: string[];
  days: { date: LocalDate; isOtherMonth: boolean }[];
  todayKey: string;
  valueKey: string | null;
  notSelectableLabel: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDay: (date: LocalDate) => void;
  isAllowedByMin: (d: LocalDate) => boolean;
  matchesMonthlyDay: (d: LocalDate) => boolean;
  shouldExclude: (d: LocalDate) => boolean;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
}

type ModeToggle =
  | { showModeButtons: false }
  | {
      showModeButtons: true;
      modeLabel: string;
      singleLabel: string;
      rangeLabel: string;
      onToggleMode: (next: SelectionMode) => void;
    };

type ExclusionToggles =
  | { showHolidayWeekendToggles: false }
  | {
      showHolidayWeekendToggles: true;
      excludeHolidaysValue: boolean;
      excludeWeekendsValue: boolean;
      isControlledHoliday: boolean;
      isControlledWeekends: boolean;
      excludeHolidaysLabel: string;
      excludeWeekendsLabel: string;
      onSetLocalExcludeHolidays: (checked: boolean) => void;
      onSetLocalExcludeWeekends: (checked: boolean) => void;
      onExcludeHolidaysChange?: (checked: boolean) => void;
      onExcludeWeekendsChange?: (checked: boolean) => void;
      onExcludeNonWorkingDaysChange?: (checked: boolean) => void;
    };

type RangeState =
  | { allowRange: false }
  | {
      allowRange: true;
      canConfirm: boolean;
      allowClear: boolean;
      clearLabel: string;
      chooseLabel: string;
      rangeStartKey: string | null;
      rangeEndKey: string | null;
      pendingStartKey: string | null;
      pendingStart: LocalDate | null;
      multiSelectedKeys: Set<string>;
      restrictSameMonthRange: boolean;
      onClear: (event?: React.MouseEvent) => void;
      onConfirm: () => void;
    };

export type DatePickerPopupProps = DatePickerPopupBase &
  ModeToggle &
  ExclusionToggles &
  RangeState;

/** Renders the calendar popup with selection UI. */
export const DatePickerPopup = React.forwardRef<
  HTMLDivElement,
  DatePickerPopupProps
>((props, ref) => {
  return (
    <CalendarPopup
      ref={ref}
      $position={props.position}
      $zIndex={props.zIndex}
      $size={props.size}
      onMouseDown={(event) => event.preventDefault()}
    >
      {props.showModeButtons && (
        <RadioRow role="radiogroup" aria-label={props.modeLabel}>
          <RadioOption>
            <RadioInput
              type="radio"
              name="date-picker-mode"
              checked={props.mode === "single"}
              onChange={() => props.onToggleMode("single")}
            />
            <RadioControl />
            <RadioLabel>{props.singleLabel}</RadioLabel>
          </RadioOption>
          <RadioOption>
            <RadioInput
              type="radio"
              name="date-picker-mode"
              checked={props.mode === "range"}
              onChange={() => props.onToggleMode("range")}
            />
            <RadioControl />
            <RadioLabel>{props.rangeLabel}</RadioLabel>
          </RadioOption>
        </RadioRow>
      )}

      {props.showHolidayWeekendToggles && (
        <ToggleGroup>
          <Checkbox
            checked={props.excludeHolidaysValue ? "checked" : "unchecked"}
            label={props.excludeHolidaysLabel}
            onChange={(checked) => {
              if (!props.isControlledHoliday) {
                props.onSetLocalExcludeHolidays(checked);
              }
              props.onExcludeHolidaysChange?.(checked);
              props.onExcludeNonWorkingDaysChange?.(
                checked || props.excludeWeekendsValue,
              );
            }}
          />
          <Checkbox
            checked={props.excludeWeekendsValue ? "checked" : "unchecked"}
            label={props.excludeWeekendsLabel}
            onChange={(checked) => {
              if (!props.isControlledWeekends) {
                props.onSetLocalExcludeWeekends(checked);
              }
              props.onExcludeWeekendsChange?.(checked);
              props.onExcludeNonWorkingDaysChange?.(
                props.excludeHolidaysValue || checked,
              );
            }}
          />
        </ToggleGroup>
      )}

      {(props.showModeButtons || props.showHolidayWeekendToggles) && (
        <SectionDivider />
      )}

      <CalendarHeader>
        <NavButton
          type="button"
          onClick={() => {
            if (props.isPrevDisabled) return;
            props.onPrevMonth();
          }}
          disabled={props.isPrevDisabled}
        >
          <CustomIcon
            name="v2/left-chevron"
            size="16px"
            color={ColorV2.Blue2}
          />
        </NavButton>
        <MonthYear>
          {props.monthNames[props.currentMonth.monthValue() - 1]}{" "}
          {props.currentMonth.year()}
        </MonthYear>
        <NavButton
          type="button"
          onClick={() => {
            if (props.isNextDisabled) return;
            props.onNextMonth();
          }}
          disabled={props.isNextDisabled}
        >
          <CustomIcon
            name="v2/right-chevron"
            size="16px"
            color={ColorV2.Blue2}
          />
        </NavButton>
      </CalendarHeader>

      <DaysGrid>
        {props.dayHeaders.map((day) => (
          <DayHeader key={day}>{day}</DayHeader>
        ))}
        {props.days.map((day, index) => {
          const cellDate = day.date;
          const key = toDayKey(cellDate);
          const rangeProps = props.allowRange ? props : null;
          const isRangeMode = props.mode === "range" && rangeProps !== null;
          const isToday = key === props.todayKey;
          const isSelected = isRangeMode
            ? (() => {
                if (rangeProps.rangeStartKey && rangeProps.rangeEndKey) {
                  return (
                    key >= rangeProps.rangeStartKey &&
                    key <= rangeProps.rangeEndKey
                  );
                }
                return (
                  !!rangeProps.pendingStartKey &&
                  key === rangeProps.pendingStartKey
                );
              })()
            : rangeProps
              ? rangeProps.multiSelectedKeys.has(key)
              : !!props.valueKey && key === props.valueKey;
          const isRangeStart = Boolean(
            isRangeMode &&
            rangeProps.rangeStartKey &&
            key === rangeProps.rangeStartKey,
          );
          const isRangeEnd = Boolean(
            isRangeMode &&
            rangeProps.rangeEndKey &&
            key === rangeProps.rangeEndKey,
          );

          const outsideStartMonthBlock =
            isRangeMode &&
            rangeProps.restrictSameMonthRange &&
            !!rangeProps.pendingStart &&
            !isSameMonthYear(cellDate, rangeProps.pendingStart);

          const disabledByMonthlyDay = !props.matchesMonthlyDay(cellDate);

          const isExcluded = props.shouldExclude(cellDate);
          const disabledBool =
            outsideStartMonthBlock ||
            !props.isAllowedByMin(cellDate) ||
            isExcluded ||
            day.isOtherMonth ||
            disabledByMonthlyDay;

          const onClickDay = (): void => {
            if (disabledBool) return;
            props.onSelectDay(cellDate);
          };

          const isHighlighted = isSelected || isRangeStart || isRangeEnd;

          return (
            <DayCell
              key={`${key}-${index}`}
              $isToday={isToday}
              $isSelected={isHighlighted}
              $isOtherMonth={day.isOtherMonth}
              $isExcluded={isExcluded}
              $disabled={disabledBool}
              onClick={onClickDay}
              aria-disabled={disabledBool ? "true" : "false"}
              title={disabledBool ? props.notSelectableLabel : undefined}
            >
              {cellDate.dayOfMonth()}
            </DayCell>
          );
        })}
      </DaysGrid>

      {props.allowRange && (
        <ActionsRow>
          {props.allowClear ? (
            <SecondaryButton
              label={props.clearLabel}
              size="small"
              onClick={props.onClear}
            />
          ) : (
            <div />
          )}
          <PrimaryButton
            label={props.chooseLabel}
            size="small"
            onClick={props.onConfirm}
            disabled={!props.canConfirm}
          />
        </ActionsRow>
      )}
    </CalendarPopup>
  );
});

DatePickerPopup.displayName = "DatePickerPopup";
